/**
 * @file
 * Header behaviours – slide-in drawer menu + inline expanding search.
 *
 * Search (desktop):
 *   .header-search holds the Drupal search block (page.navbar_search) and
 *   the toggle button. Clicking the toggle adds .is-open to .header-search –
 *   CSS transitions the width and fades the block form in. The toggle fades
 *   out via [aria-expanded="true"] in CSS.
 *
 * Search (mobile):
 *   Toggle opens the drawer instead; focus goes to the search input inside
 *   .mobile-menu__search.
 *
 * Drawer menu:
 *   Hidden via [hidden]. JS removes it, double-rAF triggers CSS slide-in.
 *   Close button, ESC, and outside-click all close it.
 */

((Drupal) => {
  // ── Live region ──────────────────────────────────────────────────────────────
  function announce(id, message) {
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      el.className = 'sr-only';
      document.body.appendChild(el);
    }
    el.textContent = '';
    setTimeout(() => {
      el.textContent = message;
    }, 50);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Behaviour: Slide-in drawer menu
  // ─────────────────────────────────────────────────────────────────────────────
  Drupal.behaviors.mobileMenu = {
    attach: (context, settings) => {
      if (document.body.dataset.menuAttached) return;
      document.body.dataset.menuAttached = '1';

      var menuToggle = document.querySelector('.menu-toggle');
      var drawer = document.getElementById('mobile-menu');
      var menuClose = drawer && drawer.querySelector('.mobile-menu__close');

      if (!menuToggle || !drawer) return;

      menuToggle.addEventListener('click', (e) => {
        e.preventDefault();
        isOpen() ? closeMenu() : openMenu();
      });

      if (menuClose) {
        menuClose.addEventListener('click', (e) => {
          e.preventDefault();
          closeMenu();
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) closeMenu();
      });

      document.addEventListener('click', (e) => {
        if (!isOpen()) return;
        if (drawer.contains(e.target)) return;
        if (menuToggle.contains(e.target)) return;
        closeMenu();
      });

      drawer.addEventListener('click', (e) => {
        var link = e.target.closest('a[href]');
        if (link && !link.getAttribute('target')) {
          setTimeout(closeMenu, 150);
        }
      });

      function isOpen() {
        return menuToggle.getAttribute('aria-expanded') === 'true';
      }

      function openMenu() {
        drawer.removeAttribute('hidden');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            drawer.classList.add('is-open');
          });
        });
        menuToggle.setAttribute('aria-expanded', 'true');
        drawer.setAttribute('aria-hidden', 'false');
        setTimeout(() => {
          if (menuClose) menuClose.focus();
        }, 50);
        announce('menu-announcer', Drupal.t('Navigation menu opened'));
      }

      function closeMenu() {
        drawer.classList.remove('is-open');
        menuToggle.setAttribute('aria-expanded', 'false');
        drawer.setAttribute('aria-hidden', 'true');
        setTimeout(() => {
          if (!isOpen()) drawer.setAttribute('hidden', '');
        }, 360);
        menuToggle.focus();
        announce('menu-announcer', Drupal.t('Navigation menu closed'));
      }
    },
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Behaviour: Main navigation disclosure (level 2 → level 3)
  //
  // Markup comes from templates/menu/menu--disclosure.html.twig. The panel is
  // collapsed in CSS off [aria-expanded], so this only flips the attribute.
  // Several groups may stay open at once, matching the editorial reference.
  // ─────────────────────────────────────────────────────────────────────────────
  Drupal.behaviors.mainMenuDisclosure = {
    attach: (context) => {
      var toggles = (context.querySelectorAll
        ? context
        : document
      ).querySelectorAll('.main-menu__toggle');

      Array.prototype.forEach.call(toggles, (toggle) => {
        if (toggle.dataset.disclosureAttached) return;
        toggle.dataset.disclosureAttached = '1';

        toggle.addEventListener('click', () => {
          var expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        });

        // Reveal the branch the visitor is currently in.
        var item = toggle.closest('.main-menu__item');
        if (item && item.classList.contains('main-menu__item--active-trail')) {
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    },
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Behaviour: Inline expanding search
  //
  // The real Kilden search block renders inside .header-search via
  // page.navbar_search. We find its input dynamically so we are not
  // coupled to any Drupal-generated ID.
  // ─────────────────────────────────────────────────────────────────────────────
  Drupal.behaviors.searchExpand = {
    attach: (context, settings) => {
      if (document.body.dataset.searchAttached) return;
      document.body.dataset.searchAttached = '1';

      var searchWrapper = document.querySelector('.header-search');
      var searchToggle = searchWrapper && searchWrapper.querySelector('.header-search__toggle');
      var menuToggle = document.querySelector('.menu-toggle');
      var mobileMenu = document.getElementById('mobile-menu');

      if (!searchToggle || !searchWrapper) return;

      var searchContent = searchWrapper.querySelector('.header-search__content');
      var mobileSearch = mobileMenu && mobileMenu.querySelector('.mobile-menu__search');
      var desktopInput;
      var blockEls;

      function isMobile() {
        return window.innerWidth < 992;
      }
      function isOpen() {
        return searchWrapper.classList.contains('is-open');
      }

      function refreshSearchElements() {
        desktopInput = searchWrapper.querySelector('input[type="text"], input[type="search"]');
        blockEls = Array.from(searchWrapper.querySelectorAll('input, button:not(.header-search__toggle), a'));
      }

      // Keep one Drupal search form in the DOM. It moves into the drawer below
      // the desktop breakpoint, avoiding duplicate form IDs and submissions.
      function syncSearchLocation() {
        if (!searchContent || !mobileSearch) return;
        if (isMobile() && searchContent.parentNode !== mobileSearch) {
          mobileSearch.appendChild(searchContent);
          searchContent.querySelectorAll('input, button, a').forEach((el) => {
            el.removeAttribute('tabindex');
          });
        } else if (!isMobile() && searchContent.parentNode !== searchWrapper) {
          searchWrapper.insertBefore(searchContent, searchToggle);
          refreshSearchElements();
          blockEls.forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
        }
      }

      refreshSearchElements();
      syncSearchLocation();

      // The desktop control starts collapsed. Mobile search stays focusable in
      // the drawer once the single form has been moved there.
      if (!isMobile()) {
        blockEls.forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
      }

      // ── Toggle click ──────────────────────────────────────────────────────────
      searchToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isMobile()) {
          if (menuToggle) {
            menuToggle.click();
            setTimeout(() => {
              var mobileInput =
                mobileMenu &&
                mobileMenu.querySelector(
                  '.mobile-menu__search input[type="text"], .mobile-menu__search input[type="search"]',
                );
              if (mobileInput) mobileInput.focus();
            }, 400);
          }
        } else {
          openSearch();
        }
      });

      // ── Click outside closes ──────────────────────────────────────────────────
      document.addEventListener('click', (e) => {
        if (isOpen() && !searchWrapper.contains(e.target)) closeSearch();
      });

      // ── ESC closes ────────────────────────────────────────────────────────────
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) closeSearch();
      });

      // ── Helpers ───────────────────────────────────────────────────────────────
      function openSearch() {
        searchWrapper.classList.add('is-open');
        searchToggle.setAttribute('aria-expanded', 'true');
        blockEls.forEach((el) => {
          el.removeAttribute('tabindex');
        });
        // Focus after CSS width transition (~500 ms)
        setTimeout(() => {
          if (desktopInput) desktopInput.focus();
        }, 520);
      }

      function closeSearch() {
        searchWrapper.classList.remove('is-open');
        searchToggle.setAttribute('aria-expanded', 'false');
        blockEls.forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        if (desktopInput) desktopInput.value = '';
        searchToggle.focus();
      }

      // ── Resize guard ──────────────────────────────────────────────────────────
      var resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (isMobile() && isOpen()) closeSearch();
          syncSearchLocation();
        }, 250);
      });
    },
  };
})(Drupal);
